import { Box, CircularProgress } from "@mui/material";
import { useJsApiLoader } from "@react-google-maps/api";
import React, { useEffect, useState, useContext } from "react";
import { HashRouter, Route, Routes } from "react-router-dom";
import { getToken, onMessage } from "firebase/messaging";
import { initialize, isFirebaseSupported } from "./firebase";
import ConfigurableValues from "./config/constants";
import Checkout from "./screens/Checkout/Checkout";
import EmailSent from "./screens/EmailSent/EmailSent";
import Favourites from "./screens/Favourites/Favourites";
import ForgotPassword from "./screens/ForgotPassword/ForgotPassword";
import Home from "./screens/Home/Home";
import Login from "./screens/Login/Login";
import LoginEmail from "./screens/LoginEmail/LoginEmail";
import MyOrders from "./screens/MyOrders/MyOrders";
import NewLogin from "./screens/NewLogin/NewLogin";
import OrderDetail from "./screens/OrderDetail/OrderDetail";
import Paypal from "./screens/Paypal/Paypal";
import Privacy from "./screens/Privacy/Privacy";
import Profile from "./screens/Profile/Profile";
import Settings from "./screens/Settings/Settings";
import GetHelp from "./screens/GetHelp/GetHelp";
import Registration from "./screens/Registration/Registration";
import PhoneNumber from "./screens/PhoneNumber/PhoneNumber";
import VerifyEmail from "./screens/VerifyEmail/VerifyEmail";
import VerifyForgotOtp from "./screens/VerifyForgotOtp/VerifyForgotOtp";
import ResetPassword from "./screens/ResetPassword/ResetPassword";
import RestaurantDetail from "./screens/RestaurantDetail/RestaurantDetail";
import Restaurants from "./screens/Restaurants/Restaurants";
import Stripe from "./screens/Stripe/Stripe";
import Terms from "./screens/Terms/Terms";
import FlashMessage from "./components/FlashMessage";
import Pickup from "./screens/Pickup/Pickup";
import * as Sentry from "@sentry/react";
import AuthRoute from "./routes/AuthRoute";
import PrivateRoute from "./routes/PrivateRoute";
import VerifyPhone from "./screens/VerifyPhone/VerifyPhone";
import UserContext from "./context/User";
import RiderRegistrationComponent from "../src/screens/RiderRegistration/RiderRegistration";
import MerchantRegistrationComponent from "../src/screens/MerchantRegistration/MerchantRegistration";
import ContactUsComponent from "./screens/Contact Us/ContactUs";
import AboutUs from "./screens/AboutUs/AboutUs";
import Klarna from "./screens/KlarnaInfo/Klarna";

import { useTranslation } from "react-i18next";

/**
 * Shown instead of a blank screen when a route throws during render. Worst case for
 * this is the post-payment order screen: the customer has already been charged, so
 * the one thing they must never see is an empty page with no way forward.
 */
const RouteErrorFallback = () => (
  <Box
    display="flex"
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    height="100vh"
    padding="24px"
    textAlign="center"
  >
    <h2 style={{ margin: "0 0 8px", fontSize: "20px" }}>Something went wrong</h2>
    <p style={{ margin: "0 0 24px", color: "#555", maxWidth: "420px" }}>
      If you have just paid, your payment went through and your order is safe. You
      can find it under your orders.
    </p>
    <a
      href="#/orders"
      style={{
        padding: "14px 28px",
        background: "#111",
        color: "#fff",
        borderRadius: "8px",
        textDecoration: "none",
        fontWeight: 600,
      }}
    >
      View my orders
    </a>
  </Box>
);

/**
 * GoogleMapsLoader component handles Google Maps API loading and Firebase messaging
 * @param {React.ReactNode} children - Child components to render after maps load
 * @param {string[]} LIBRARIES - Google Maps libraries to load
 * @param {string} GOOGLE_MAPS_KEY - Google Maps API key
 */
const GoogleMapsLoader = ({ children, LIBRARIES, GOOGLE_MAPS_KEY }) => {
  const [message, setMessage] = useState(null);
  const { t, i18n } = useTranslation();

  // Initialize Firebase messaging and handle push notifications
  useEffect(() => {
    const initializeFirebase = async () => {
      if (await isFirebaseSupported()) {
        const messaging = initialize();
        Notification.requestPermission()
          .then(() => {
            getToken(messaging, {
              vapidKey:
                "BOpVOtmawD0hzOR0F5NQTz_7oTlNVwgKX_EgElDnFuILsaE_jWYPIExAMIIGS-nYmy1lhf2QWFHQnDEFWNG_Z5w",
            })
              .then((token) => {
                localStorage.setItem("messaging-token", token);
              })
              .catch((err) => {
                console.log("getToken error", err);
              });
          })
          .catch(console.log);

        // Listen for foreground messages and display localized notifications
        onMessage(messaging, function (payload) {
          // Customize notification here
          const { title, body } = payload.notification;
          var localizedBody = body;
          var orderNo = "";
          var localizedTitle = title;
          // Create localized message based on language direction (RTL for Arabic)
          const createMessage = (orderNo, localizedTitle, localizedBody) => {
            return i18n.language === "ar"
              ? `${orderNo} ${localizedTitle} ${localizedBody}`
              : `${localizedTitle} ${localizedBody} ${orderNo}`;
          };
          if (title.startsWith("Order status:")) {
            localizedTitle = t(title);
            const orderIdIndex = body.indexOf("Order ID");
            orderNo = body.slice(orderIdIndex + 9).trim();
            localizedBody = t("Order ID");
            setMessage(createMessage(orderNo, localizedTitle, localizedBody));
          } else if (title === "Order placed") {
            localizedTitle = t("orderPlaced");
            const orderIdIndex = body.indexOf("Order ID");
            orderNo = body.slice(orderIdIndex + 9).trim();
            localizedBody = t("Order ID");
            setMessage(createMessage(orderNo, localizedTitle, localizedBody));
          } else {
            console.log(localizedTitle, localizedBody);
            setMessage(`${localizedTitle} ${localizedBody}`);
          }
        });
      }
    };
    initializeFirebase();
  }, [t, i18n]);

  const handleClose = () => {
    setMessage(null);
  };

  // Load Google Maps JavaScript API with required libraries
  const { isLoaded, loadError } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_MAPS_KEY,
    libraries: LIBRARIES,
  });
  
  console.log("Google Maps API Key:", GOOGLE_MAPS_KEY);
  console.log("Libraries:", LIBRARIES);
  console.log("isLoaded:", isLoaded);
  console.log("loadError:", loadError);
  
  if (loadError) {
    console.error("Google Maps failed to load:", loadError);
    // Skip Google Maps and render children anyway
    return (
      <>
        <Sentry.ErrorBoundary fallback={<RouteErrorFallback />}>
          {children}
        </Sentry.ErrorBoundary>
        <FlashMessage
          severity={"info"}
          alertMessage={message}
          open={message !== null}
          handleClose={handleClose}
        />
      </>
    );
  }
  if (!isLoaded && !loadError) {
    return (
      <Box
        component="div"
        display="flex"
        alignItems="center"
        justifyContent="center"
        height="100vh"
        width="100vw"
      >
        <CircularProgress color="primary" />
      </Box>
    );
  }

  return (
    <>
      <Sentry.ErrorBoundary fallback={<RouteErrorFallback />}>
        {children}
      </Sentry.ErrorBoundary>
      <FlashMessage
        severity={"info"}
        alertMessage={message}
        open={message !== null}
        handleClose={handleClose}
      />
    </>
  );
};



/**
 * Main App component - Entry point for the Enatega multivendor food delivery web app
 * Handles routing, authentication, and Google Maps integration
 */
function App() {
  const { GOOGLE_MAPS_KEY, LIBRARIES } = ConfigurableValues();
  const { isLoggedIn } = useContext(UserContext);

  // Render app with routing
  return (
    <HashRouter>
      <GoogleMapsLoader GOOGLE_MAPS_KEY={GOOGLE_MAPS_KEY} LIBRARIES={LIBRARIES}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/restaurant-list" element={<Restaurants />} />
          <Route path="/restaurant/:slug" element={<RestaurantDetail />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/pickup" element={<Pickup />} />
          <Route
            path={"/login"}
            element={
              <AuthRoute>
                <Login />
              </AuthRoute>
            }
          />
          <Route
            path={"/registration"}
            element={
              <AuthRoute>
                <Registration />
              </AuthRoute>
            }
          />
          <Route
            path={"/new-login"}
            element={
              <AuthRoute>
                <NewLogin />
              </AuthRoute>
            }
          />
          <Route
            path={"/login-email"}
            element={
              <AuthRoute>
                <LoginEmail />
              </AuthRoute>
            }
          />
          <Route
            path={"/verify-email"}
            element={
              <AuthRoute>
                <VerifyEmail />
              </AuthRoute>
            }
          />
          <Route
            path={"/new-password"}
            element={
              <AuthRoute>
                <ResetPassword />
              </AuthRoute>
            }
          />
          <Route
            path={"/phone-number"}
            element={
              <PrivateRoute>
                <PhoneNumber />
              </PrivateRoute>
            }
          />
          <Route
            path={"/verify-phone"}
            element={
              <PrivateRoute>
                <VerifyPhone />
              </PrivateRoute>
            }
          />
          <Route
            path={"/forgot-password"}
            element={
              <AuthRoute>
                <ForgotPassword />
              </AuthRoute>
            }
          />
          <Route
            path={"/verify-forgot-otp"}
            element={
              <AuthRoute>
                <VerifyForgotOtp />
              </AuthRoute>
            }
          />
          <Route
            path={"/email-sent"}
            element={
              <AuthRoute>
                <EmailSent />
              </AuthRoute>
            }
          />
          <Route
            path={"/orders"}
            element={
              <PrivateRoute>
                <MyOrders />
              </PrivateRoute>
            }
          />
          <Route
            path={"/profile"}
            element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            }
          />
          <Route
            path={"/settings"}
            element={
              <PrivateRoute>
                <Settings />
              </PrivateRoute>
            }
          />
          <Route
            path={"/get-help"}
            element={
              <PrivateRoute>
                <GetHelp />
              </PrivateRoute>
            }
          />
          <Route
            path={"/checkout"}
            element={isLoggedIn ? <Checkout /> : <Login />}
          />
          <Route
            path={"/order-detail/:id"}
            element={
              <PrivateRoute>
                <OrderDetail />
              </PrivateRoute>
            }
          />
          <Route
            path={"/paypal"}
            element={
              <PrivateRoute>
                <Paypal />
              </PrivateRoute>
            }
          />
          <Route
            path={"/stripe"}
            element={
              <PrivateRoute>
                <Stripe />
              </PrivateRoute>
            }
          />
          <Route
            path={"/favourite"}
            element={
              <PrivateRoute>
                <Favourites />
              </PrivateRoute>
            }
          />
          <Route
            path={"/riderRegistration"}
            element={<RiderRegistrationComponent />}
          />
          <Route
            path={"/merchantRegistration"}
            element={<MerchantRegistrationComponent/>}
          />
          <Route
            path={"/ContactUs"}
            element={<ContactUsComponent/>}
          />
          <Route
            path={"/AboutUs"}
            element={<AboutUs/>}
          />
          <Route 
          path={"KlarnaInfo"}
          element={<Klarna/>}/>
          <Route path="*" element={<Home />} />
        </Routes>
      </GoogleMapsLoader>
    </HashRouter>
  );
}

export default Sentry.withProfiler(App);
